import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Employee, ReservedCourseItem } from "../../../../types";
import CustomSearch from "../../../search";
import { Color } from "../../../../constants/color";
import { ZIndex } from "../../../../constants/z-index";
import SingleParticipant from "./single-participant";
import Label from "../../../label";
import Button from "../../../button/button";
import { Icons } from "../../../../constants/icons";
import React, { useCallback, useEffect, useState } from "react";
import { BreakpointsQuery } from "../../../../constants/device";
import { reserveCourseDate } from "../../../../features/reservation/utils";
import {
  filterParticipantAlreadyReserved,
  getCFListFromEmployees,
} from "../../../../utils/utils";
import BookModal from "../../../modal/book-course";
import { getHiredEmployeeByCompanyId } from "../../../../features/employee/utils";
import Feedback from "../../../modal/feedback";
import { axiosInstance } from "../../../../provider/authProvider";
import { BE_BASE_URL } from "../../../../utils/config";
import { CombinedReservedCourse } from "..";
import { getEmployeeForReservationByRangeDate } from "../../../../features/employee/utils";
import { contieneTesto } from "../../../../features/certificate/pipeline";
import { avvisoUnico } from "../../../../features/notifiche/avvisi";
import { useSolaLettura } from "../../../../features/user/solaLettura";
import { Users } from "lucide-react";
import { StatoVuoto } from "../../../attestati/stato-vuoto";

interface Props {
  course: CombinedReservedCourse;
  show: boolean;
}

const ParticipantsWrapper = styled.div<{
  $open: boolean;
}>`
  display: ${($open) => ($open.$open ? "flex" : "none")};
  transition: display 2s;
  flex-direction: column;
  align-items: center;
  row-gap: 0.8rem;
  max-height: 24rem;
  width: 100%;
  background-color: ${Color.CERAMIC};
  box-shadow: 0 0 8px 6px ${Color.SHADOW};
  border-bottom-left-radius: 1.2rem;
  border-bottom-right-radius: 1.2rem;
  padding-top: 1.6rem;
  padding-bottom: 1.6rem;
  ::-webkit-scrollbar {
    position: relative;
    display: flex;
    left: 4rem;
    width: 0.6rem;
    border-radius: 4rem;
  }
  ::-webkit-scrollbar-button {
    display: none;
  }
  ::-webkit-scrollbar-track {
    border-radius: 4rem;
    background-color: ${Color.LIGHT_BLUE};
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 4rem;
    background-color: ${Color.LIGHT_BLUE};
  }
  ::-webkit-scrollbar-track-piece {
    border-radius: 4rem;
    background-color: ${Color.GREY};
  }
  @media ${BreakpointsQuery.tablet} {
    width: 990px;
  }
`;

const TableHeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1.4rem;
  margin-left: 231px;
  margin-right: auto;
  width: 50rem;
  @media ${BreakpointsQuery.tablet} {
    margin-left: 107px;
  }
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.3rem;
  min-width: max-content;
  overflow-x: visible;
  overflow-y: auto;
  max-height: 22rem;
`;

const LogMessage = styled.textarea`
  margin: 0.5rem 0;
  min-height: 3rem;
  min-width: 50rem;
  max-width: 50rem;
  border: 2px solid ${Color.LIGHT_BLUE};
  border-radius: 0.5rem;
  font-family: inherit;
  font-size: 0.9rem;
`;

const EditButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  column-gap: 4rem;
  margin-top: 0.4rem;
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  justify-items: flex-end;
  width: 1000px;
  column-gap: 40px;
`;

const ButtonWithTooltip = styled.div`
  position: relative;
  display: inline-block;

  &:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }
`;

const Tooltip = styled.div`
  visibility: hidden;
  position: absolute;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${Color.LIGHT_BLUE};
  color: ${Color.WHITE};
  text-align: center;
  padding: 8px 10px;
  border-radius: 6px;
  width: max-content;
  max-width: 200px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: ${ZIndex.TOOLTIP};
  font-size: 0.8rem;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: ${Color.LIGHT_BLUE} transparent transparent transparent;
  }
`;

const Participants = ({ show, course }: Props) => {
  const { t } = useTranslation();
  const [edit, setEdit] = useState(false);
  const [participantList, setList] = useState<Employee[]>([]);
  const [newParticipantList, setNewList] = useState<Employee[]>([]);
  const [toRemove, setToRemove] = useState<Employee[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [modified, setModified] = useState<boolean>(false);
  const [employeeList, setEmployeeList] = useState<Employee[]>([]);
  const [logMessage, setLogMessage] = useState("");
  // Il testo cercato e se i partecipanti sono arrivati: servono a spiegare la
  // tabella vuota, che senza ricerca o prima del caricamento non si spiega.
  const [ricerca, setRicerca] = useState("");
  const [caricato, setCaricato] = useState(false);
  const courseDates = course.date ? Array.from(course.date) : [];

  //const [editAllowed, setEditAllowed] = useState<boolean>(
  //  course.editable !== undefined ? course.editable : true
  //);
  // In sola lettura (abbonamento disattivato, ODOO 300) la prenotazione si consulta ma non si
  // modifica, e il perche' detto all'utente e' quello, non le 48 ore.
  const solaLettura = useSolaLettura();
  const editAllowed =
    !solaLettura && (course.editable !== undefined ? course.editable : true);
  const motivoBlocco = solaLettura
    ? t("sola-lettura-comando")
    : t("reserved-courses-edit-disabled-tooltip");

  const [dateSet, SetDateSet] = useState<Set<Date>>(new Set());

  useEffect(() => {
    if (course && courseDates.length > 0) {
      const datesArray: Date[] = [];
      courseDates.forEach((cd) => datesArray.push(cd.date));
      SetDateSet(new Set(datesArray));

      const courseId = courseDates.length > 0 ? courseDates[0].courseId : "";
      getEmployeeForReservationByRangeDate(courseId, datesArray)!.then((response) => {
        setEmployeeList(response);
        setList(Object.assign([], course.participants));
        setNewList(Object.assign([], course.participants));
        setCaricato(true);
      });
    }
  }, [course.variationId]);

  useEffect(() => {
    // Aggiorniamo la lista dei partecipanti visualizzati quando cambia newParticipantList
    setList([...newParticipantList]);
  }, [newParticipantList]);

  //useEffect(() => {
  // Update editAllowed when course.isEditable changes
  //const newValue = course.editable !== undefined ? course.editable : true;
  //setEditAllowed(newValue);
  //}, [course.editable]);

  function removeParticipant(participants: Employee[], index: number) {
    setNewList(newParticipantList.filter((el) => el !== participants[index]));
    if (toRemove.includes(participants[index])) {
      setToRemove(toRemove.filter((el) => el !== participants[index]));
    } else {
      const array = [...toRemove];
      array.push(participants[index]);
      setToRemove(array);
    }
  }

  function searchParticipant(input: string) {
    setRicerca(input);
    // Cognome e nome si cercano in entrambi gli ordini, come fa la ricerca
    // libera sul discente in Gestione corsi.
    const filteredList: Employee[] = newParticipantList.filter(
      (el) =>
        contieneTesto(`${el.lastName} ${el.firstName}`, input) ||
        contieneTesto(`${el.firstName} ${el.lastName}`, input)
    );
    if (input.length !== 0) {
      setList(filteredList);
    } else if (input.length === 0) {
      setList(newParticipantList);
    }
  }

  const handleSaveLogMessage = async (text: string, variationId: number) => {
    const message = {
      variationId: variationId,
      text: text,
    };
    const response = await axiosInstance.post(
      `${BE_BASE_URL}/reservation-logs`,
      message,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    if (response.status >= 400) {
      avvisoUnico(
        "reserved-courses-log-salvataggio",
        "error",
        "Errore imprevisto durante il salvataggio del messaggio di log, riprovare e se il problema persiste contattare l'assistenza."
      );
    }
  };

  // Chi si puo' aggiungere: gli idonei che non sono gia' nella prenotazione.
  // Gli altri la finestra non li mostra, ma le servono per dire perche' non
  // c'e' nessuno da aggiungere.
  const aggiungibili = filterParticipantAlreadyReserved(
    employeeList,
    newParticipantList,
    toRemove
  );

  return (
    <ParticipantsWrapper $open={show}>
      <Row>
        <CustomSearch
          name={t("reserved-courses-detail-header-name")}
          labelProps={{
            color: Color.LIGHT_BLUE,
            text: t("reserved-courses-detail-header-name"),
            fontWeight: "bolder",
          }}
          width={"500px"}
          onSearch={(input) => searchParticipant(input)}
        />
      </Row>
      <TableHeaderContainer>
        <Label
          text={t("reserved-courses-detail-header-name")}
          color={Color.LIGHT_BLUE}
          textAlign={"left"}
          textTransform={"uppercase"}
          fontWeight={"normal"}
          fontSize={"12px"}
          width={"250px"}
        />
        <Label
          text={t("reserved-courses-detail-header-headquarter")}
          color={Color.LIGHT_BLUE}
          textAlign={"center"}
          textTransform={"uppercase"}
          fontWeight={"normal"}
          fontSize={"12px"}
          width={"200px"}
        />
        <Label
          text={t("reserved-courses-detail-header-job-description")}
          color={Color.LIGHT_BLUE}
          textAlign={"center"}
          textTransform={"uppercase"}
          fontWeight={"normal"}
          fontSize={"12px"}
          width={"200px"}
        />
      </TableHeaderContainer>
      <ListWrapper>
        {caricato && ricerca.trim() !== "" && participantList.length === 0 && (
          <StatoVuoto
            icona={Users}
            titolo={t("reserved-courses-detail-ricerca-vuoto")}
            suggerimento={t("reserved-courses-detail-ricerca-vuoto-suggerimento")}
          />
        )}
        {participantList.map((singleParticipant, index) => {
          return (
            <SingleParticipant
              participant={singleParticipant}
              key={index}
              edit={edit}
              toBeRemoved={toRemove.includes(singleParticipant)}
              onDelete={() => {
                removeParticipant(participantList, index);
                setModified(true);
              }}
            />
          );
        })}
      </ListWrapper>
      {modified && (
        <LogMessage
          placeholder={t("reserved-courses-detail-log-placeholder")}
          value={logMessage}
          rows={3}
          onChange={(e) => setLogMessage(e.target.value)}
        />
      )}
      <EditButtonsContainer>
        <ButtonWithTooltip>
          <div
            onClick={() => {
              if (!editAllowed) {
                avvisoUnico(
                  "reserved-courses-edit-disabled",
                  "info",
                  motivoBlocco
                );
              }
            }}
          >
            {" "}
            <Button
              label={t("reserved-courses-detail-button-edit")}
              color={Color.RED}
              icon={Icons.BUTTONS.EDIT}
              onClick={() => {
                //console.log("Edit button clicked - editAllowed:", editAllowed);
                if (editAllowed) {
                  setEdit(!edit);
                }
              }}
              disabled={!editAllowed}
            />
          </div>
          {!editAllowed && (
            <Tooltip className="tooltip">
              {motivoBlocco}
            </Tooltip>
          )}
        </ButtonWithTooltip>

        <ButtonWithTooltip>
          <div
            onClick={() => {
              if (!editAllowed) {
                avvisoUnico(
                  "reserved-courses-edit-disabled",
                  "info",
                  motivoBlocco
                );
              }
            }}
          >
            {" "}
            <Button
              label={t("reserved-courses-detail-button-add")}
              color={Color.GREEN}
              icon={Icons.BUTTONS.PLUS}
              onClick={() => {
                //console.log("Add button clicked - editAllowed:", editAllowed);
                if (editAllowed) {
                  setShowModal(true);
                }
              }}
              disabled={!editAllowed}
            />
          </div>
          {!editAllowed && (
            <Tooltip className="tooltip">
              {motivoBlocco}
            </Tooltip>
          )}
        </ButtonWithTooltip>
        {modified && (
          <Button
            label={t("reserved-courses-detail-button-rollback")}
            color={Color.RED}
            icon={Icons.BUTTONS.RESET}
            onClick={() => {
              setEdit(false);
              setList(course.participants);
              setNewList(participantList);
              setModified(false);
              setToRemove([]);
            }}
          />
        )}
        {modified && (
          <Button
            label={t("reserved-courses-detail-button-save")}
            color={Color.LIGHT_BLUE}
            icon={Icons.COMMONS.CALENDAR}
            onClick={async () => {
              if (logMessage.trim() === "") {
                avvisoUnico(
                  "reserved-courses-salvataggio",
                  "warning",
                  t("reserved-courses-detail-log-empty")
                );
                return;
              }
              let newList = newParticipantList;
              newList = newList.filter(
                (el) => toRemove.find((pr) => pr.cf === el.cf) === undefined
              );
              const reservationResult = await reserveCourseDate(
                course.variationId,
                getCFListFromEmployees(newList)
              );

              if (!reservationResult.success) {
                // Il messaggio arriva dal backend: dice quale prenotazione non e' passata.
                avvisoUnico(
                  "reserved-courses-salvataggio",
                  "error",
                  reservationResult.message ||
                    "Si è verificato un errore durante la prenotazione del corso."
                );
              }

              setSaveError(!reservationResult.success);
              setShowDialog(true);

              // Only reload the page if the operation was successful
              if (reservationResult.success) {
                setTimeout(() => {
                  setShowDialog(false);
                  window.location.reload();
                }, 2000);
              } else {
                // Just close the dialog after showing the error
                setTimeout(() => {
                  setShowDialog(false);
                }, 2000);
              }
              handleSaveLogMessage(logMessage, course.variationId);
            }}
          />
        )}
      </EditButtonsContainer>
      {showModal && (
        <BookModal
          participants={aggiungibili}
          giaNellaPrenotazione={employeeList.filter(
            (el) => !aggiungibili.includes(el)
          )}
          show={showModal}
          setShow={() => setShowModal(false)}
          variationId={course.variationId}
          isEditReserve={true}
          editParticipantList={(newPrList: Employee[]) => {
            // Correggiamo la gestione dei nuovi partecipanti selezionati
            if (newPrList && newPrList.length > 0) {
              // Creiamo una nuova lista che combina i partecipanti esistenti con i nuovi
              let updatedList = [...newParticipantList];

              // Aggiungiamo solo partecipanti che non sono già nella lista
              newPrList.forEach((newParticipant) => {
                const exists = updatedList.some(
                  (p) => p.cf === newParticipant.cf
                );
                if (!exists) {
                  updatedList.push(newParticipant);
                }
              });

              setNewList(updatedList);
              setList(updatedList);
              setModified(true);
            }
          }}
          style={{
            position: "fixed",
            top: "160px",
          }}
          courseDetails={{
            duration: course.duration,
            date1: courseDates[0],
            date2: courseDates[1] ? courseDates[1] : undefined,
          }}
        />
      )}
      {showDialog && <Feedback saveError={saveError} />}
    </ParticipantsWrapper>
  );
};

export default Participants;
